import { msalInstance } from './msal'
import { loginRequest, graphConfig } from './config'

async function getToken() {
  const account = msalInstance.getAllAccounts()[0]
  if (!account) throw new Error('No active account')

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
    })
    return response.accessToken
  } catch (err) {
    // Fallback to interactive if silent fails
    const response = await msalInstance.acquireTokenPopup({
      ...loginRequest,
      account,
    })
    return response.accessToken
  }
}

async function fetchGraph(endpoint: string, options: RequestInit = {}) {
  const token = await getToken()
  const res = await fetch(`https://graph.microsoft.com/v1.0${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })
  if (!res.ok) {
    throw new Error(`Graph error: ${res.statusText}`)
  }
  return res.json()
}

export async function getMe() {
  const user = await fetchGraph(
    '/me?$select=displayName,jobTitle,officeLocation,mail',
  )
  let avatar = ''
  try {
    // Try to get photo
    const token = await getToken()
    const photoRes = await fetch(
      'https://graph.microsoft.com/v1.0/me/photo/$value',
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    )
    if (photoRes.ok) {
      const blob = await photoRes.blob()
      avatar = URL.createObjectURL(blob)
    }
  } catch (e) {
    // ignore missing photo
  }

  return {
    name: user.displayName,
    title: user.jobTitle || 'Team Member',
    location: user.officeLocation || 'Independence Home Loans',
    email: user.mail || user.userPrincipalName,
    avatar,
  }
}

export async function checkAdminAccess() {
  if (!graphConfig.adminGroupId) return false
  try {
    const res = await fetchGraph(`/me/memberOf`)
    const groups = res.value || []
    return groups.some((g: any) => g.id === graphConfig.adminGroupId)
  } catch (e) {
    return false
  }
}

// Helper to resolve the site ID from the URL
let cachedSiteId = ''
async function getSiteId() {
  if (cachedSiteId) return cachedSiteId
  try {
    const url = new URL(graphConfig.siteUrl)
    const hostname = url.hostname
    const path = url.pathname
    const res = await fetchGraph(`/sites/${hostname}:${path}?$select=id`)
    cachedSiteId = res.id
    return cachedSiteId
  } catch (e) {
    console.error('Failed to resolve SharePoint site ID', e)
    throw e
  }
}

export async function getListItems(listName: string) {
  const siteId = await getSiteId()
  const res = await fetchGraph(
    `/sites/${siteId}/lists/${listName}/items?expand=fields`,
  )
  return res.value.map((item: any) => ({
    id: item.id,
    ...item.fields,
  }))
}

export async function addListItem(listName: string, fields: any) {
  const siteId = await getSiteId()
  const res = await fetchGraph(`/sites/${siteId}/lists/${listName}/items`, {
    method: 'POST',
    body: JSON.stringify({ fields }),
  })
  return { id: res.id, ...res.fields }
}
